import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    StarIcon,
    MinusIcon,
    PlusIcon,
    ShieldCheckIcon,
    TruckIcon,
    CheckCircleIcon,
    ArrowLeftIcon,
    HeartIcon,
    ShareIcon,
    ShoppingBagIcon,
    Bars3Icon,
    HomeIcon,
    TagIcon,
    Squares2X2Icon,
    BellIcon,
    MagnifyingGlassIcon,
    UserIcon,
    ArrowRightOnRectangleIcon,
    ChevronUpIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { useAppContext } from './context/AppContext';
import { API } from './config';
import logo from './images/FINAL-DESIGN-LOGO.png';
import ProductCard from './components/ProductCard';

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart, user, cart, logout } = useAppContext();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [isLiked, setIsLiked] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('description');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

    useEffect(() => {
        fetchProduct();
        fetchCategories();
        fetchCategories();
        fetchRelatedProducts();
        fetchReviews();
        window.scrollTo(0, 0);
    }, [id]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/products/${id}`);
            setProduct(response.data);
            if (response.data.sizes && response.data.sizes.length > 0) {
                // Don't auto-select size, let user choose
            }
        } catch (error) {
            console.error('Failed to fetch product:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchRelatedProducts = async () => {
        try {
            // Just fetching some random products for now as "related"
            const response = await axios.get(`${API}/products?limit=4`);
            setRelatedProducts(response.data.filter(p => p.id !== id).slice(0, 4));
        } catch (error) {
            console.error('Failed to fetch related products:', error);
        }
    };

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${API}/products/${id}/reviews`);
            setReviews(response.data);
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate('/login');
            return;
        }
        setIsSubmittingReview(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/products/${id}/reviews`, newReview, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewReview({ rating: 5, comment: '' });
            setShowReviewForm(false);
            fetchReviews();
            fetchProduct();
        } catch (error) {
            console.error('Failed to submit review:', error);
            alert('Failed to submit review');
        } finally {
            setIsSubmittingReview(false);
        }
    };

    const handleAddToCart = async () => {
        if (product.is_customizable) {
            navigate(`/customize/${product.id}`);
            return;
        }

        if (product.sizes && product.sizes.length > 0 && !selectedSize) {
            // Toast error could go here
            alert('Please select a size');
            return;
        }
        await addToCart(product.id, quantity, selectedSize);
    };

    if (loading) return (
        <div className="min-h-screen flex justify-center items-center bg-gray-50">
            <div className="animate-spin w-12 h-12 border-4 border-[#E31E24] border-t-transparent rounded-full"></div>
        </div>
    );

    if (!product) return null;

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed top-0 left-0 z-50
                w-64 bg-[#111114] text-gray-400 flex flex-col
                h-screen
                transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    <div className="p-6 flex-1 overflow-y-auto">
                        {/* Logo Area */}
                        <div className="flex items-center gap-3 mb-10 px-2">
                            <img src={logo} alt="Illustra" className="h-8 w-auto brightness-0 invert" />
                        </div>

                        <nav className="space-y-2">
                            <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Menu</p>

                            <Link
                                to="/products"
                                className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white"
                            >
                                <Squares2X2Icon className="h-5 w-5 mr-3" />
                                <span className="font-medium">All Products</span>
                            </Link>

                            <div className="pt-4">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Categories</p>
                                {categories.map((category) => (
                                    <Link
                                        key={category.id}
                                        to={`/products?category_id=${category.id}`}
                                        className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white"
                                    >
                                        <TagIcon className="h-5 w-5 mr-3" />
                                        <span className="font-medium">{category.name}</span>
                                    </Link>
                                ))}
                            </div>

                            <div className="pt-4 mt-4 border-t border-gray-800">
                                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Main Site</p>
                                <Link to="/" className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1F1F23] rounded-xl transition-all">
                                    <HomeIcon className="h-5 w-5 mr-3" />
                                    <span className="font-medium">Home</span>
                                </Link>
                            </div>
                        </nav>
                    </div>

                    {/* User Profile Section at Bottom */}
                    {user && (
                        <div className="p-4 border-t border-gray-800 bg-[#0c0c0e]">
                            <div className="relative">
                                <button
                                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                                    className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#1F1F23] transition-colors group"
                                >
                                    <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-sm shrink-0">
                                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div className="flex-1 text-left overflow-hidden">
                                        <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                    </div>
                                    <ChevronUpIcon className={`h-5 w-5 text-gray-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {isProfileOpen && (
                                    <div className="absolute bottom-full left-0 w-full mb-2 bg-[#1F1F23] border border-gray-800 rounded-xl shadow-xl overflow-hidden p-1">
                                        <Link
                                            to="/profile"
                                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2f] hover:text-white rounded-lg transition-colors"
                                        >
                                            <UserIcon className="h-5 w-5" />
                                            Profile
                                        </Link>
                                        <button
                                            onClick={logout}
                                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-400 hover:bg-[#2a2a2f] hover:text-red-300 rounded-lg transition-colors"
                                        >
                                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0 overflow-x-hidden lg:ml-64">
                {/* Header */}
                <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                            <Bars3Icon className="h-6 w-6" />
                        </button>
                        <nav className="hidden md:flex items-center text-sm text-gray-500">
                            <Link to="/products" className="hover:text-[#E31E24] transition-colors">Products</Link>
                            <span className="mx-2">/</span>
                            <span className="text-gray-900 font-medium truncate max-w-[200px]">{product.title}</span>
                        </nav>
                    </div>

                    <div className="flex items-center gap-4 lg:gap-6">
                        <div className="relative hidden md:block w-64">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-[#E31E24]/20 text-sm"
                            />
                        </div>

                        <div className="flex items-center gap-4">
                            <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                <BellIcon className="h-6 w-6" />
                            </button>



                            {!user && (
                                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-[#E31E24]">
                                    Sign In
                                </Link>
                            )}
                        </div>
                    </div>
                </header>

                <div className="p-3 lg:p-4 max-w-[1600px]">
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                        <div className="xl:col-span-3 space-y-6">
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                                    {/* Product Images */}
                                    <div className="p-4 bg-gray-50">
                                        <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white shadow-sm mb-4 relative group max-w-xs mx-auto">
                                            <img
                                                src={product.images?.[selectedImage] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'}
                                                alt={product.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                            <button
                                                onClick={() => setIsLiked(!isLiked)}
                                                className="absolute top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                            >
                                                {isLiked ? (
                                                    <HeartSolidIcon className="h-6 w-6 text-red-500" />
                                                ) : (
                                                    <HeartIcon className="h-6 w-6 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {product.images && product.images.length > 1 && (
                                            <div className="grid grid-cols-4 gap-4">
                                                {product.images.map((img, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setSelectedImage(idx)}
                                                        className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${selectedImage === idx ? 'border-[#E31E24] shadow-md' : 'border-transparent hover:border-gray-300'}`}
                                                    >
                                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Product Info */}
                                    <div className="p-4 lg:p-6 flex flex-col">
                                        <div className="mb-auto">
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded-full uppercase tracking-wide">
                                                    {product.category || 'New Arrival'}
                                                </span>
                                                <div className="flex items-center text-yellow-400 text-sm font-medium">
                                                    <StarSolidIcon className="h-4 w-4 mr-1" />
                                                    <span>{product.rating ? product.rating.toFixed(1) : '0.0'} ({product.review_count || 0} reviews)</span>
                                                </div>
                                            </div>

                                            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight">
                                                {product.title}
                                            </h1>

                                            <div className="flex items-baseline gap-3 mb-4">
                                                <span className="text-xl font-bold text-[#E31E24]">₹{product.price}</span>
                                                {product.old_price && (
                                                    <span className="text-lg text-gray-400 line-through">₹{product.old_price}</span>
                                                )}
                                            </div>

                                            <div className="prose prose-sm text-xs text-gray-600 mb-6 leading-relaxed break-words">
                                                <p>{product.description?.replace(/<[^>]*>/g, ' ')}</p>
                                            </div>

                                            {/* Size Selection */}
                                            {product.sizes && product.sizes.length > 0 && (
                                                <div className="mb-6">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="font-bold text-gray-900 text-sm">Select Size</label>
                                                        <button className="text-xs text-[#E31E24] hover:underline font-medium">Size Guide</button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-3">
                                                        {product.sizes.map(size => (
                                                            <button
                                                                key={size}
                                                                onClick={() => setSelectedSize(size)}
                                                                className={`h-8 min-w-[2rem] px-2 rounded-lg border text-xs font-medium transition-all flex items-center justify-center ${selectedSize === size
                                                                    ? 'border-[#E31E24] text-[#E31E24] bg-orange-50 shadow-sm ring-1 ring-[#E31E24]'
                                                                    : 'border-gray-200 text-gray-600 hover:border-[#E31E24] hover:text-[#E31E24]'
                                                                    }`}
                                                            >
                                                                {size}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Quantity & Add to Cart */}
                                            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                                                <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 w-fit">
                                                    <button
                                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                        className="p-1.5 hover:bg-gray-100 rounded-l-lg transition-colors text-gray-600"
                                                    >
                                                        <MinusIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                    <span className="w-10 text-center font-bold text-gray-900 text-sm">{quantity}</span>
                                                    <button
                                                        onClick={() => setQuantity(quantity + 1)}
                                                        className="p-1.5 hover:bg-gray-100 rounded-r-lg transition-colors text-gray-600"
                                                    >
                                                        <PlusIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={handleAddToCart}
                                                    className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 ${product.is_customizable
                                                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                                                        : "bg-[#111114] hover:bg-[#2a2a2f] text-white"
                                                        }`}
                                                >
                                                    {product.is_customizable ? (
                                                        <>
                                                            <PhotoIcon className="h-4 w-4" />
                                                            Start Designing
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ShoppingBagIcon className="h-4 w-4" />
                                                            Add to Cart
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Features */}
                                            <div className="grid grid-cols-2 gap-3 pt-6 border-t border-gray-100">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-green-50 rounded-lg text-green-600">
                                                        <TruckIcon className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-sm">Fast Delivery</h4>
                                                        <p className="text-xs text-gray-500 mt-0.5">Reliable shipping partners</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                        <ShieldCheckIcon className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-sm">Secure Payment</h4>
                                                        <p className="text-xs text-gray-500 mt-0.5">100% protected</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tabs Section (Description, Reviews, etc.) */}
                            <div className="mt-6 bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden p-4 lg:p-6">
                                <div className="flex items-center gap-6 border-b border-gray-100 mb-4">
                                    {['Description', 'Reviews', 'Shipping'].map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab.toLowerCase())}
                                            className={`pb-4 text-sm font-bold transition-all relative ${activeTab === tab.toLowerCase()
                                                ? 'text-[#E31E24]'
                                                : 'text-gray-500 hover:text-gray-800'
                                                }`}
                                        >
                                            {tab}
                                            {activeTab === tab.toLowerCase() && (
                                                <motion.div
                                                    layoutId="activeTab"
                                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E31E24]"
                                                />
                                            )}
                                        </button>
                                    ))}
                                </div>

                                <div className="min-h-[200px]">
                                    {activeTab === 'description' && (
                                        <div className="prose prose-gray max-w-none break-words">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4">Product Details</h3>
                                            <div dangerouslySetInnerHTML={{ __html: (product.product_details || product.description)?.replace(/\n/g, '<br/>') }} />

                                            {product.bullet_points && (
                                                <>
                                                    <h3 className="text-lg font-bold text-gray-900 mt-6 mb-4">{product.bullet_points_title || 'Features'}</h3>
                                                    <ul className="list-disc pl-5 space-y-2 text-gray-600">
                                                        {product.bullet_points.split('\n').map((point, idx) => (
                                                            point.trim() && <li key={idx}>{point.replace(/^[•\-\*]\s*/, '')}</li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}


                                        </div>
                                    )}
                                    {activeTab === 'reviews' && (
                                        <div>
                                            <div className="flex items-center justify-between mb-6">
                                                <h3 className="text-lg font-bold text-gray-900">Customer Reviews ({reviews.length})</h3>
                                                <button
                                                    onClick={() => setShowReviewForm(!showReviewForm)}
                                                    className="text-[#E31E24] font-medium hover:underline"
                                                >
                                                    Write a Review
                                                </button>
                                            </div>

                                            {showReviewForm && (
                                                <motion.form
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    onSubmit={handleSubmitReview}
                                                    className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100"
                                                >
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-bold text-gray-900 mb-2">Rating</label>
                                                        <div className="flex gap-1">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <button
                                                                    key={star}
                                                                    type="button"
                                                                    onClick={() => setNewReview({ ...newReview, rating: star })}
                                                                    className="focus:outline-none transition-transform hover:scale-110"
                                                                >
                                                                    {star <= newReview.rating ? (
                                                                        <StarSolidIcon className="h-6 w-6 text-yellow-400" />
                                                                    ) : (
                                                                        <StarIcon className="h-6 w-6 text-gray-300" />
                                                                    )}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-bold text-gray-900 mb-2">Review</label>
                                                        <textarea
                                                            required
                                                            rows="4"
                                                            value={newReview.comment}
                                                            onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#E31E24] focus:border-[#E31E24] bg-white text-sm"
                                                            placeholder="Share your thoughts about this product..."
                                                        />
                                                    </div>
                                                    <div className="flex justify-end gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowReviewForm(false)}
                                                            className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors text-sm"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            disabled={isSubmittingReview}
                                                            className="bg-[#111114] text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-[#2a2a2f] transition-all shadow-lg disabled:opacity-50"
                                                        >
                                                            {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                                                        </button>
                                                    </div>
                                                </motion.form>
                                            )}

                                            <div className="space-y-6">
                                                {reviews.length === 0 ? (
                                                    <p className="text-gray-500 text-center py-8">No reviews yet. Be the first to review this product!</p>
                                                ) : (
                                                    reviews.map((review) => (
                                                        <div key={review.id} className="border-b border-gray-50 pb-6 last:border-0">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                                                                        {review.user_name ? review.user_name.charAt(0).toUpperCase() : 'U'}
                                                                    </div>
                                                                    <span className="font-bold text-gray-900 text-sm">{review.user_name || 'Anonymous'}</span>
                                                                </div>
                                                                <span className="text-xs text-gray-400">
                                                                    {new Date(review.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <div className="flex text-yellow-400 mb-2">
                                                                {[...Array(5)].map((_, i) => (
                                                                    i < review.rating ? (
                                                                        <StarSolidIcon key={i} className="h-3 w-3" />
                                                                    ) : (
                                                                        <StarIcon key={i} className="h-3 w-3 text-gray-300" />
                                                                    )
                                                                ))}
                                                            </div>
                                                            <p className="text-gray-600 text-sm">
                                                                {review.comment}
                                                            </p>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'shipping' && (
                                        <div className="prose prose-gray max-w-none">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4">Shipping Information</h3>
                                            <p className="text-gray-600">
                                                Shipping costs are calculated based on your order quantity and delivery location.
                                                Orders are typically processed within 1-2 business days.
                                            </p>
                                            <div className="grid grid-cols-1 gap-6 mt-6">
                                                <div className="p-4 border border-gray-100 rounded-xl">
                                                    <h4 className="font-bold text-gray-900 mb-2">Standard Delivery</h4>
                                                    <p className="text-sm text-gray-600">5-7 business days</p>
                                                    <p className="text-sm font-bold text-[#E31E24] mt-2">Starts from ₹100</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Related Products Sidebar */}
                        <div className="xl:col-span-1">
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sticky top-24">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">You May Also Like</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                                    {relatedProducts.map((related) => (
                                        <ProductCard key={related.id} product={related} onQuickView={() => navigate(`/product/${related.id}`)} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ProductDetailPage;
